﻿import {KeysetQuery, KeysetQueryResult} from "@2d-front-monorepo/core";
import {ParkingType} from "../../nominalTypes/ParkingType";
import {PermitId} from "../../nominalTypes/PermitId";
import {ResidencyId} from "../../nominalTypes/ResidencyId";
import {VehicleId} from "../../nominalTypes/VehicleId";
import {CreatePermitArguments} from "../../types/arguments/CreatePermitArguments";
import {GetPermitsByAddressArguments} from "../../types/arguments/GetPermitsByAddressArguments";
import {ReplaceDriverArguments} from "../../types/arguments/ReplaceDriverArguments";
import {ReplaceResidencyArguments} from "../../types/arguments/ReplaceResidencyArguments";
import {ReplaceVehicleArguments} from "../../types/arguments/ReplaceVehicleArguments";
import {CreatePermitResult} from "../../types/CreatePermitResult";
import {ListPermitsResult} from "../../types/ListPermitsResult";
import {PaymentHistoryEntry} from "../../types/PaymentHistoryEntry";
import {PermitResidencyResult} from "../../types/PermitResidencyResult";
import {PermitResult} from "../../types/PermitResult";
import {UnparkingRefund} from "../../types/QueryUnparkingOperationResult";
import IPermitsPermits from "../IPermitsPermits";
import {NETWORK_RETRY} from "../networkRetry";
import {PermitsFrontClient} from "./PermitsFrontClient";
import {PermitParkingTypeData, ResidencyBehavior, VehicleBehavior} from "../../types/PermitParkingTypeData";

export class PermitsPermits implements IPermitsPermits {
	_client: PermitsFrontClient;

	constructor(client: PermitsFrontClient) {
		this._client = client;
	}

	queryUnpark(permitId: PermitId, signal?: AbortSignal | undefined): Promise<UnparkingRefund[]> {
		return this._client.jsonFetch(`permits/${permitId}/unpark`, "GET", null, {
			signal: signal
		});
	}

	confirmUnpark(permitId: PermitId, amount: number, signal?: AbortSignal | undefined): Promise<void> {
		return this._client.nullFetch(`permits/${permitId}/unpark`, "PUT", null, {
			query: {
				"amount": amount.toString(10)
			},
			signal: signal
		});
	}

	getResidencyBehavior(permitId: PermitId, residencyId: ResidencyId, signal?: AbortSignal | undefined): Promise<ResidencyBehavior> {
		return this._client.jsonFetch(`permits/${permitId}/residencies/${residencyId}/behavior`, "GET", null, {
			signal: signal
		});
	}

	getVehicleBehavior(permitId: PermitId, vehicleId: VehicleId, signal?: AbortSignal | undefined): Promise<VehicleBehavior> {
		return this._client.jsonFetch(`permits/${permitId}/vehicles/${vehicleId}/behavior`, "GET", null, {
			signal: signal
		});
	}

	getParkingTypeData(parkingType: ParkingType, signal?: AbortSignal | undefined): Promise<PermitParkingTypeData> {
		return this._client.jsonFetch(`permits/parking-type/${parkingType}`, "GET", null, {
			signal: signal
		});
	}

	list(signal?: AbortSignal | undefined): Promise<ListPermitsResult> {
		return this._client.jsonFetch("permits", "GET", null, {
			signal: signal
		});
	}

	read(permitId: PermitId, signal?: AbortSignal | undefined): Promise<PermitResult> {
		return this._client.jsonFetch(`permits/${permitId}`, "GET", null, {
			signal: signal
		});
	}

	archived(query?: KeysetQuery<PermitId> | undefined, signal?: AbortSignal | undefined): Promise<KeysetQueryResult<PermitResult>> {
		return this._client.jsonFetch("permits/archived", "GET", query, {
			signal: signal
		});
	}

	bill(permitId: PermitId, signal?: AbortSignal | undefined): Promise<void> {
		return this._client.nullFetch(`permits/${permitId}/bill`, "PUT", null, {
			signal: signal
		});
	}

	cancel(permitId: PermitId, signal?: AbortSignal | undefined): Promise<void> {
		return this._client.nullFetch(`permits/${permitId}/cancel`, "PUT", null, {
			signal: signal
		});
	}

	setAutoRenew(permitId: PermitId, enabled: boolean, signal?: AbortSignal | undefined): Promise<void> {
		return this._client.nullFetch(`permits/${permitId}/auto-renew`, "PUT", null, {
			query: {
				"enabled": String(enabled)
			},
			signal: signal
		});
	}

	bulkCancel(permitIds: PermitId[], signal?: AbortSignal | undefined): Promise<void> {
		return this._client.nullFetch(`permits/cancel`, "PUT", permitIds, {
			signal: signal
		});
	}

	create(data: CreatePermitArguments, signal?: AbortSignal | undefined): Promise<CreatePermitResult> {
		return this._client.jsonFetch("permits", "POST", data, {
			signal: signal,
			...NETWORK_RETRY
		});
	}

	findByAddress(data: GetPermitsByAddressArguments, signal?: AbortSignal | undefined): Promise<PermitResidencyResult[]> {
		return this._client.jsonFetch("permits/find-by/address", "POST", data, {
			signal: signal
		});
	}

	getInvoice(permitId: PermitId, signal?: AbortSignal | undefined): Promise<string> {
		return this._client.textFetch(`permits/${permitId}/invoice`, "GET", null, {
			signal: signal
		});
	}

	getPayments(permitId: PermitId, query?: KeysetQuery<number> | undefined, signal?: AbortSignal | undefined): Promise<KeysetQueryResult<PaymentHistoryEntry>> {
		return this._client.jsonFetch(`permits/${permitId}/payments`, "GET", query, {
			signal: signal
		});
	}

	listPayments(query?: KeysetQuery<number> | undefined, signal?: AbortSignal | undefined): Promise<KeysetQueryResult<PaymentHistoryEntry>> {
		return this._client.jsonFetch("permits/payments", "GET", query, {
			signal: signal
		});
	}

	replaceDriver(permitId: PermitId, data: ReplaceDriverArguments, signal?: AbortSignal | undefined): Promise<void> {
		return this._client.nullFetch(`permits/${permitId}/replace-driver`, "PUT", data, {
			signal: signal
		});
	}

	replaceResidency(permitId: PermitId, data: ReplaceResidencyArguments, signal?: AbortSignal | undefined): Promise<void> {
		return this._client.nullFetch(`permits/${permitId}/replace-residency`, "PUT", data, {
			signal: signal
		});
	}

	replaceVehicle(permitId: PermitId, data: ReplaceVehicleArguments, signal?: AbortSignal | undefined): Promise<void> {
		return this._client.nullFetch(`permits/${permitId}/replace-vehicle`, "PUT", data, {
			signal: signal
		});
	}

	submitForReview(permitId: PermitId, signal?: AbortSignal | undefined): Promise<void> {
		return this._client.nullFetch(`permits/${permitId}/re-submit`, "PUT", null, {
			signal: signal
		});
	}
}
